import { LayoutElement } from "@entaconsulting/form";
import Editor from "@monaco-editor/react";
import debounce from "lodash/debounce";
import monaco, { editor } from "monaco-editor";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { ErrorData } from "../types";

type FormEditorProps = {
  json: string;
  modelUri: string;
  onValidate: (markers: monaco.editor.IMarker[]) => void;
  onChange: (value: string | undefined) => void;
  validateFn?: (elements: LayoutElement[], jsonLines: string[]) => void;
  errors?: ErrorData[];
};

const FormEditor = (props: FormEditorProps) => {
  const {
    json,
    modelUri,
    onValidate: handleValidate,
    onChange: handleChange,
    validateFn,
    errors,
  } = props;

  // const [isJsonValidated, setIsJsonValidated] = useState<boolean>(false);

  const monacoEditorRef = useRef<editor.IStandaloneCodeEditor>();
  const monacoRef = useRef<typeof monaco>();

  const runValidations = useCallback(() => {
    if (!monacoEditorRef.current || !monacoRef.current) return;
    const model = monacoEditorRef.current.getModel();
    const value = model?.getValue();
    const jsonLines = value?.split(/\r\n|\r|\n/) || [];

    if (value && validateFn) {
      //we don't need this to fail if the json is invalid
      try {
        const editedJsonObject = JSON.parse(value);
        validateFn(editedJsonObject, jsonLines);
      } catch (_) {}
    }
  }, [validateFn]);

  useEffect(() => {
    let markers: monaco.editor.IMarkerData[];

    if (!monacoEditorRef.current || !monacoRef.current) return;

    const model = monacoEditorRef.current.getModel();
    if (!model) return;

    if (!errors || errors.length === 0) {
      markers = [];
    } else {
      if (!monacoEditorRef.current || !monacoRef.current) return;
      const model = monacoEditorRef.current.getModel();
      const value = model?.getValue();
      const jsonLines = value?.split(/\r\n|\r|\n/) || [];

      if (!jsonLines) return;

      markers = errors.map((errorData) => {
        const startColumn =
          (jsonLines[errorData.line - 1]?.match(/\t/g) || []).length + 1;
        return {
          severity: 8,
          startLineNumber: errorData.line,
          startColumn,
          endLineNumber: errorData.line,
          endColumn: 1000000, // Long enough to reach the end of the line
          message: errorData.message,
        };
      });
    }

    monacoRef.current.editor.setModelMarkers(
      model,
      "mySpecialLanguage",
      markers
    );
  }, [errors]);

  const runValidationsDebounced = useMemo(
    () => debounce(runValidations, 300),
    [runValidations]
  );

  const handleChangeInternal = useCallback(
    (value: string | undefined) => {
      runValidationsDebounced();
      handleChange(value);
    },
    [handleChange, runValidationsDebounced]
  );

  useEffect(() => {
    if (validateFn) {
      runValidations();
    }
  }, [runValidations, validateFn]);

  return (
    <Editor
      defaultLanguage="json"
      defaultValue={json}
      value={json}
      onValidate={handleValidate}
      onChange={handleChangeInternal}
      onMount={(editor, monaco) => {
        monacoEditorRef.current = editor;
        monacoRef.current = monaco;
        runValidations();
      }}
      path={modelUri}
      options={{
        quickSuggestions: {
          // other: "inline",
          // comments: "inline",
          strings: "on",
        },
      }}
    />
  );
};
export default FormEditor;
